import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfPen,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

/**
 * Darky (Gifts) - Gift-wrapped cards with hidden images
 *
 * Layout: 2 landscape pages, 5x2 grid.
 * Page 1: Gift boxes with colored wrapping ribbons.
 * Page 2: Answer key with shadow+flipped images and mirrored text labels.
 *
 * Migrated from C# EduFrame.Img10.Generators.Darky
 */
export class Darky implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);
    const imageSize = { width: size.width * 0.7, height: size.width * 0.9 };
    const wrapSize = PdfUnit.FromMillimeters(5);
    const wrapLength = PdfUnit.FromMillimeters(10);
    const labelHeight = PdfUnit.FromMillimeters(8);

    const page1 = pdf.addPage(true);
    const page2 = pdf.addPage(true);

    // Collect all labels for font adjustment
    const allLabels: string[] = [];
    for (const item of opts.list) {
      const label = item.label || '';
      if (label) allLabels.push(label);
    }
    const labelFont =
      allLabels.length > 0
        ? page2.adjustFont(imageSize.width, allLabels, GUtil.baseFont)
        : GUtil.baseFont;

    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 2; y++) {
        const ind = y * 5 + x;
        if (ind >= opts.list.length) continue;

        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        const plusWidth = PdfUnit.FromMillimeters(opts.data.random.next(0, 5));
        const plusHeight = PdfUnit.FromMillimeters(opts.data.random.next(0, 15));

        // Pick 2 random PiccoloColors (Fisher-Yates shuffle)
        const colorIndices = [0, 1, 2, 3, 4];
        for (let i = colorIndices.length - 1; i > 0; i--) {
          const j = opts.data.random.next(0, i + 1);
          [colorIndices[i], colorIndices[j]] = [colorIndices[j], colorIndices[i]];
        }
        const col1 = GUtil.piccoloColors[colorIndices[0]];
        const col2 = GUtil.piccoloColors[colorIndices[1]];

        const imageRect = new PdfRect(
          rect.x + rect.width / 2 - imageSize.width / 2,
          rect.y + rect.height / 2 - imageSize.height / 2,
          imageSize.width,
          imageSize.height
        );

        const objRect = new PdfRect(
          imageRect.x - plusWidth,
          imageRect.y - plusHeight,
          imageSize.width + 2 * plusWidth,
          imageSize.height + 2 * plusHeight
        );

        // Cut line
        page1.drawRectangle(GUtil.cutPen, null, rect);

        // Ribbons
        const w1 = new PdfRect(
          objRect.x,
          objRect.y + objRect.height / 2 - wrapSize / 2,
          objRect.width,
          wrapSize
        );
        const w2 = new PdfRect(
          objRect.x + objRect.width / 2 - wrapSize / 2,
          objRect.y,
          wrapSize,
          objRect.height
        );
        const topCenter = new PdfPoint(objRect.x + objRect.width / 2, objRect.y);
        const p1 = new PdfPoint(topCenter.x - wrapLength, topCenter.y - wrapLength);
        const p2 = new PdfPoint(topCenter.x + wrapLength, topCenter.y - wrapLength);

        // Collect per-item labels
        const label = opts.list[ind].label || '';
        const labels = [label].filter((l: string) => l);

        // Page 1: Gift box
        page1.drawRectangle(null, new PdfBrush(col1), objRect);
        page1.drawRectangle(null, new PdfBrush(col2), w1);
        page1.drawRectangle(null, new PdfBrush(col2), w2);
        const ribbonPen = new PdfPen(col2, wrapSize);
        page1.drawLine(ribbonPen, topCenter, p1);
        page1.drawLine(ribbonPen, topCenter, p2);
        page1.drawRectangle(GUtil.basePen, null, objRect);

        // Page 2: Shadow+flip image + mirrored labels
        const imgRect = new PdfRect(
          imageRect.x,
          imageRect.y,
          imageRect.width,
          imageRect.height - labels.length * labelHeight
        );

        let img = opts.list[ind].image;
        if (img) {
          img = await img.toShadow(pdf);
          img = await img.flipH(pdf);
          page2.drawImageCenter(img, imgRect);
        }

        for (let i = 0; i < labels.length; i++) {
          const r = new PdfRect(
            imgRect.x,
            imgRect.y + imgRect.height + i * labelHeight,
            imgRect.width,
            labelHeight
          );

          page2.save();
          page2.translateTransform(r.x + r.width / 2, r.y + r.height / 2);
          page2.scaleTransform(-1, 1);
          page2.drawString(
            labels[i],
            labelFont,
            PdfBrush.Black,
            null,
            new PdfRect(-r.width / 2, -r.height / 2, r.width, r.height),
            ETextAlign.Center
          );
          page2.restore();
        }
      }
    }
  }
}
